import {
  EApplicantType,
  ECreditApplicationStep,
  EDocumentStatus,
  EElectronicSignatureStatus,
  EFinancingProgram,
  EOriginSystemId,
  ETasksStatus,
} from '@if/core/types'
import { formatDate } from '@if/core/utils'
import {
  Cancel as CancelIcon,
  CheckCircle as CheckCircleIcon,
  ContentCopy as ContentCopyIcon,
  ContentPasteGo,
  PendingActions as PendingActionsIcon,
  Print as PrintIcon,
  ReportProblem,
} from '@mui/icons-material'
import { Button, ButtonGroup, Divider, IconButton, Paper, Stack, Tooltip, Typography } from '@mui/material'
import { useCreditApplicationData } from '@src/contexts/CreditApplicationContext'
import { useMerchantFinancingConfig } from '@src/data/api/config/financing-config-api'
import {
  useGetApplicantLaw134IsCompleted,
  useGetCoApplicantLaw134IsCompleted,
  useIsAbleToPrintCvt,
  useIsApplicantLaw134Required,
  useIsCoApplicantLaw134Required,
} from '@src/data/selectors/credit-selectors'
import { useHasReceivedCvtDocument } from '@src/data/selectors/document-selectors'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { BaseWorksheetDto, Constants, CreditApplication, User } from '../../../data/types'

interface Props {
  handleExpenseClicked: (data: EApplicantType) => void
  onClickGenerateCvt: () => void
  onClickPrintCvt: () => void
  onClickResendElectronicSign: () => void
  onClickSendElectronicSign: () => void
  onClickBankAccount: () => void
  creditApplication: CreditApplication
  worksheet: BaseWorksheetDto | null
  electronicSignatureStatus: EElectronicSignatureStatus | null
  user: User | null
  editDisabled: boolean
  disableResendLinkButton: boolean
}

const FinancingContractSection = ({
  handleExpenseClicked,
  onClickGenerateCvt,
  onClickPrintCvt,
  onClickResendElectronicSign,
  onClickSendElectronicSign,
  onClickBankAccount,
  creditApplication,
  worksheet,
  electronicSignatureStatus,
  user,
  editDisabled,
  disableResendLinkButton,
}: Props) => {
  const { t } = useTranslation()

  const getIcon = (isCompleted: boolean) => {
    return isCompleted ? (
      <CheckCircleIcon fontSize="small" color="success" />
    ) : (
      <PendingActionsIcon fontSize="small" color="warning" />
    )
  }

  const financingProgramConfig = Constants.financingProgramConfigs[creditApplication.financingProgramId]

  const getButtonText = (isCompleted: boolean | null | undefined) => {
    return isCompleted ? '' : t('financeContract.toBeCompleted')
  }

  const isAbleToPrintCvt = useIsAbleToPrintCvt()
  const [merchantFinancingConfig] = useMerchantFinancingConfig(creditApplication.merchantId, creditApplication.financingProgramId)
  const cvtManagedByThirdParty = merchantFinancingConfig?.cvtManagedByThirdParty ?? false
  const applicantLaw134IsCompleted = useGetApplicantLaw134IsCompleted()
  const coApplicantLaw134IsCompleted = useGetCoApplicantLaw134IsCompleted()
  const applicantLaw134IsRequired = useIsApplicantLaw134Required()
  const coApplicantLaw134IsRequired = useIsCoApplicantLaw134Required()

  // Get documents and tasks from context
  const { documents: documentList, requiredDocuments: documentRequestedList, tasks: taskList } = useCreditApplicationData()
  const hasReceivedCvtDocument = useHasReceivedCvtDocument(documentList)

  const steps = [ECreditApplicationStep.CVT, ECreditApplicationStep.Financing, ECreditApplicationStep.Credit]

  const hasTaskPending = taskList.some(
    (item) =>
      item.requiredBeforeStep &&
      (steps as string[]).includes(item.requiredBeforeStep) &&
      item.status === ETasksStatus.ToDo,
  )

  const hasDocumentPending = documentRequestedList.some(
    (documentRequested) =>
      !documentList.some(
        (document) =>
          document.status === EDocumentStatus.Approved &&
          documentRequested.typeId === document.typeId &&
          documentRequested.applicantType === document.applicantType,
      ) &&
      documentRequested.requiredBeforeStep &&
      (steps as string[]).includes(documentRequested.requiredBeforeStep),
  )

  const getElectronicSignatureStatus = () => {
    switch (electronicSignatureStatus) {
      case EElectronicSignatureStatus.Pending:
      case EElectronicSignatureStatus.CreateContract:
      case EElectronicSignatureStatus.StartSignatureProject:
      case EElectronicSignatureStatus.AwaitingSignature:
        return getIcon(false)
      case EElectronicSignatureStatus.Completed:
        return getIcon(true)
      case EElectronicSignatureStatus.Cancel:
      case EElectronicSignatureStatus.CancelSignatureProject:
        return <CancelIcon fontSize="small" color="error" />
      default:
        return <ReportProblem fontSize="small" color="warning" />
    }
  }

  const isElectronicSignatureDisabled = () => {
    return (
      !electronicSignatureStatus ||
      electronicSignatureStatus === EElectronicSignatureStatus.Pending ||
      electronicSignatureStatus === EElectronicSignatureStatus.Completed ||
      electronicSignatureStatus === EElectronicSignatureStatus.CancelSignatureProject ||
      electronicSignatureStatus === EElectronicSignatureStatus.Cancel ||
      editDisabled ||
      hasReceivedCvtDocument
    )
  }

  const showSendElectronicSignButton = () => {
    return (
      creditApplication?.originSystemId === EOriginSystemId.MerchantDashboard &&
      !electronicSignatureStatus &&
      !hasReceivedCvtDocument
    )
  }

  const [cvtCopied, setCvtCopied] = useState(false)

  const handleCopyCvtNumber = () => {
    void navigator.clipboard.writeText(String(creditApplication.cvtNumber))
    setCvtCopied(true)
    setTimeout(() => setCvtCopied(false), 1000)
  }

  return (
    <Paper elevation={3} sx={{ borderRadius: 2, p: 2, mt: 3, mb: 3 }}>
      <Stack alignItems="left" justifyContent="space-between">
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Typography variant="h5" component="span" gutterBottom>
            {t('financeContract.financeContract')}
          </Typography>
          <Stack direction="row" spacing={2}>
            {financingProgramConfig.useConsigno && creditApplication?.cvtNumber && (
              <ButtonGroup>
                <Tooltip
                  title={`${t('electronicSignature.status')}: ${
                    electronicSignatureStatus ? t(`electronicSignature.${electronicSignatureStatus}`) : 'N/A'
                  }`}
                  placement="top"
                >
                  <span>
                    {showSendElectronicSignButton() && (
                      <Button
                        endIcon={getElectronicSignatureStatus()}
                        variant="outlined"
                        disabled={editDisabled}
                        onClick={onClickSendElectronicSign}
                      >
                        {t('electronicSignature.sendLink')}
                      </Button>
                    )}
                    {!showSendElectronicSignButton() && (
                      <Button
                        endIcon={getElectronicSignatureStatus()}
                        variant="outlined"
                        disabled={disableResendLinkButton || isElectronicSignatureDisabled()}
                        onClick={onClickResendElectronicSign}
                      >
                        {t('electronicSignature.resendLink')}
                      </Button>
                    )}
                  </span>
                </Tooltip>
              </ButtonGroup>
            )}
            <Button
              variant="outlined"
              disabled={editDisabled || !user?.rights.canEditCreditApp}
              onClick={onClickBankAccount}
              endIcon={getIcon(creditApplication.paymentBankAccount !== null)}
            >
              {t('bankAccount.title')}
            </Button>
            {applicantLaw134IsRequired && (
              <Button
                variant="outlined"
                endIcon={getIcon(applicantLaw134IsCompleted ?? false)}
                onClick={() => handleExpenseClicked(EApplicantType.Applicant)}
                disabled={editDisabled || !user?.rights.canEditCreditApp}
              >
                {t('financeContract.law134Applicant')} {getButtonText(applicantLaw134IsCompleted)}
              </Button>
            )}
            {creditApplication.coApplicant !== null && coApplicantLaw134IsRequired && (
              <Button
                variant="outlined"
                onClick={() => handleExpenseClicked(EApplicantType.CoApplicant)}
                endIcon={getIcon(coApplicantLaw134IsCompleted)}
                disabled={editDisabled || !user?.rights.canEditCreditApp}
              >
                {t('financeContract.law134CoApplicant')} : {getButtonText(coApplicantLaw134IsCompleted)}
              </Button>
            )}
          </Stack>
        </Stack>
        {!isAbleToPrintCvt && !cvtManagedByThirdParty && (
          <Typography color="error" component="div" gutterBottom>
            {t('financeContract.errorMessage')}
          </Typography>
        )}
      </Stack>

      {isAbleToPrintCvt && !cvtManagedByThirdParty && (
        <>
          <Divider flexItem sx={{ height: 15, alignSelf: 'center' }} />

          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={1}
            alignItems="top"
            marginTop={1}
            justifyContent="space-evenly"
            divider={<Divider orientation="vertical" flexItem />}
          >
            {!creditApplication?.cvtNumber && (
              <Button
                endIcon={
                  <ContentPasteGo fontSize="large" color={user?.rights.canEditCreditApp ? 'primary' : 'inherit'} />
                }
                color="inherit"
                sx={{ pl: 0 }}
                onClick={onClickGenerateCvt}
                disabled={
                  !user?.rights.canEditCreditApp ||
                  hasTaskPending ||
                  hasDocumentPending ||
                  creditApplication.financingProgramId === EFinancingProgram.Personal ||
                  editDisabled
                }
              >
                {t('financeContract.generateCvt')}
              </Button>
            )}
            {creditApplication?.cvtNumber && (
              <Stack direction="row" alignItems="center">
                <Button
                  endIcon={<PrintIcon fontSize="large" color="primary" />}
                  color="inherit"
                  sx={{ pl: 0 }}
                  onClick={onClickPrintCvt}
                >
                  CVT : #{creditApplication?.cvtNumber}
                </Button>
                <Tooltip title={cvtCopied ? t('common.copied') : t('common.copy')} placement="top">
                  <IconButton onClick={handleCopyCvtNumber}>
                    {cvtCopied ? <CheckCircleIcon color="success" fontSize='small' /> : <ContentCopyIcon color="primary" fontSize='small' />}
                  </IconButton>
                </Tooltip>
              </ Stack>
            )}
            <Typography variant="body1" component="div" gutterBottom>
              {t('worksheet.deliveryOn')} : {formatDate(worksheet?.deliveryOn)}
            </Typography>
            <Typography variant="body1" component="div" gutterBottom>
              {t('worksheet.firstPaymentOn')} : {formatDate(worksheet?.firstPaymentOn)}
            </Typography>
            <Typography variant="body1" component="div" gutterBottom>
              {t('worksheet.lastPaymentDate')} : {formatDate(worksheet?.lastPaymentOn)}
            </Typography>
          </Stack>
        </>
      )}
    </Paper>
  )
}

export default FinancingContractSection
